/**
 * Backhaul connectivity (the site RUT956, §4). Leads with the ACTIVE SIM's live signal (bars + RSRP +
 * quality); the standby SIM shows its last-surveyed reading with a timestamp — a single-modem RUT can
 * only measure the network it's registered to, so the standby figure is only as fresh as the last
 * survey/switch. Self-polling; renders demo values today, real edge values once the RUT connects.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './auth';
import type { Connectivity } from './api';

const POLL_MS = 5000;

/** RSRP → 1..4 bars + a label. Typical LTE: ≥ -80 excellent, -90 good, -100 fair, below poor. */
function grade(rsrp: number | null): { level: number; label: string } {
  if (rsrp === null) return { level: 0, label: '—' };
  if (rsrp >= -80) return { level: 4, label: 'Excellent' };
  if (rsrp >= -90) return { level: 3, label: 'Good' };
  if (rsrp >= -100) return { level: 2, label: 'Fair' };
  return { level: 1, label: 'Poor' };
}
function ago(ts: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(ts).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}
function Bars({ level }: { level: number }): JSX.Element {
  return (
    <span className={`sig-bars lvl${level}`} aria-label={`signal ${level} of 4`}>
      {[1, 2, 3, 4].map((i) => (
        <span key={i} className={`bar ${i <= level ? 'on' : ''}`} />
      ))}
    </span>
  );
}
const dbm = (v: number | null) => (v === null ? '—' : `${Math.round(v)} dBm`);

export function ConnectivityWidget({ controllerId }: { controllerId: string }): JSX.Element | null {
  const { api } = useAuth();
  const [conn, setConn] = useState<Connectivity | null>(null);
  const [failed, setFailed] = useState(false);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      setConn(await api.getConnectivity(controllerId));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [api, controllerId]);

  useEffect(() => {
    void load();
    timer.current = window.setInterval(() => void load(), POLL_MS);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [load]);

  if (failed && !conn) return null; // no connectivity surface — stay out of the way
  const active = conn?.active ?? null;
  const g = grade(active?.rsrp ?? null);

  return (
    <div className="panel conn-widget">
      <div className="conn-head">
        <span className="section-label" style={{ margin: 0 }}>
          Backhaul · RUT956
        </span>
        {active?.failover_count_24h != null && (
          <span className="conn-fo">
            {active.failover_count_24h} failover{active.failover_count_24h === 1 ? '' : 's'} / 24h
          </span>
        )}
      </div>

      {!active ? (
        <div className="faint" style={{ padding: '6px 0' }}>
          Awaiting gateway — no SIM telemetry yet.
        </div>
      ) : (
        <div className="conn-grid">
          <div className="conn-sim active">
            <div className="conn-top">
              <span className="conn-op">{active.operator ?? 'SIM 1'}</span>
              <span className="pill good">
                <span className="dot" />
                active
              </span>
            </div>
            <div className="conn-sig">
              <Bars level={g.level} />
              <div>
                <div className="conn-rsrp">{dbm(active.rsrp)}</div>
                <div className={`conn-qual q${g.level}`}>{g.label}</div>
              </div>
            </div>
            <div className="conn-metrics">
              <span>RSRQ {active.rsrq == null ? '—' : `${Math.round(active.rsrq)} dB`}</span>
              <span>SINR {active.sinr == null ? '—' : `${Math.round(active.sinr)} dB`}</span>
              {active.data_used_mb != null && (
                <span>{Math.round(active.data_used_mb)} MB · 24h</span>
              )}
            </div>
          </div>

          <div className="conn-sim standby">
            <div className="conn-top">
              <span className="conn-op">{conn?.standby?.operator ?? 'Standby SIM'}</span>
              <span className="pill idle">standby</span>
            </div>
            {conn?.standby ? (
              <>
                <div className="conn-sig">
                  <Bars level={grade(conn.standby.rsrp).level} />
                  <div>
                    <div className="conn-rsrp">{dbm(conn.standby.rsrp)}</div>
                    <div className="conn-qual q0">last surveyed {ago(conn.standby.ts)}</div>
                  </div>
                </div>
                <div className="conn-metrics">
                  <span className="faint">standby signal is only as fresh as the last survey</span>
                </div>
              </>
            ) : (
              <div className="faint" style={{ paddingTop: 6 }}>
                Not yet surveyed. A single-modem RUT reads the standby SIM only when it switches to
                it.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
